// HonestPause — the 90-second breathing dot.
//
// This is the dose sheet's only lyrical element. Everything else is
// operational. The dot pulses, copy cycles every 30s for the first
// minute and a half, and Save is fully active the whole time —
// nothing about this component blocks action. It's not a barrier; it's
// a beat.
//
// Why: most recovery apps either nag the user into not logging or
// treat the log as pure data entry. The Honest Pause does neither.
// Adds zero friction to the action and one moment of intentionality
// to the experience. We track only whether the user paused (waited
// the full 90s OR explicitly tapped "I took a breath") so they can
// see a private monthly stat — it's their accomplishment, not ours.
//
// Accessibility: respects prefers-reduced-motion (dot stays visible
// but stops pulsing; copy still cycles via interval). Dismiss button
// is keyboard-focusable. The dot itself is a button so screen-reader
// users can dismiss it without hunting.

import { useEffect, useState } from 'react';

interface HonestPauseProps {
  /** Reset on each sheet open. */
  open: boolean;
  /** Notified when the user crosses the "paused" threshold (either by
   *  waiting the full 90s or explicitly tapping "I took a breath").
   *  Idempotent — safe to call multiple times. */
  onPaused: () => void;
}

const COPY_LINES = [
  'You showed up. Take one breath before you decide.',
  'Notice what’s here without naming it good or bad.',
  'What would tonight-you want now-you to do?',
];

const TOTAL_MS = 90_000; // 90 seconds total
const COPY_CYCLE_MS = 30_000; // each line shows for 30 seconds

export function HonestPause({ open, onPaused }: HonestPauseProps) {
  const [dismissed, setDismissed] = useState(false);
  const [elapsed, setElapsed] = useState(0); // ms since open

  // Reset on each sheet open. The pause is per-open, not per-session.
  useEffect(() => {
    if (open) {
      setDismissed(false);
      setElapsed(0);
    }
  }, [open]);

  // Drive the elapsed counter while the sheet is open and the dot
  // hasn't been dismissed and the 90s window hasn't expired. 250ms
  // tick is plenty for the copy-line transition timing.
  useEffect(() => {
    if (!open || dismissed || elapsed >= TOTAL_MS) return;
    const id = window.setInterval(() => {
      setElapsed((prev) => {
        const next = prev + 250;
        if (next >= TOTAL_MS) {
          // Auto-mark paused at the end of the window.
          onPaused();
          return TOTAL_MS;
        }
        return next;
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [open, dismissed, elapsed, onPaused]);

  if (!open || dismissed || elapsed >= TOTAL_MS) return null;

  const lineIdx = Math.min(COPY_LINES.length - 1, Math.floor(elapsed / COPY_CYCLE_MS));
  const line = COPY_LINES[lineIdx];

  function handleBreath() {
    onPaused();
    setDismissed(true);
  }

  return (
    <div className="honest-pause" role="status" aria-live="polite">
      <button
        type="button"
        className="honest-pause-dot"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss breathing pause"
      />
      <p className="honest-pause-line">{line}</p>
      <button
        type="button"
        className="honest-pause-breath"
        onClick={handleBreath}
      >
        I took a breath
      </button>
    </div>
  );
}
